import { AbstractControl, FormArray, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import isEmail from 'validator/es/lib/isEmail';

export class SafariValidators {
  static _recursionControl: boolean;

  static regexUSZipCode = /^([0-9]{5})(?:[-\s]*([0-9]{4}))?$/;
  static formatUS5ZipCode = '99999';
  static formatUS9ZipCode = '99999-9999';

  static zipCodeUS5Digit(): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } | null => {
      const rx = new RegExp(SafariValidators.regexUSZipCode);
      const format = SafariValidators.formatUS5ZipCode;

      return rx.test(c.value)
        ? null
        : {
            zipCode: {
              valid: false,
              format
            }
          };
    };
  }
  // this is not used.  We should probably just remove it.
  static invalidCharacters(regex: string, message: string): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } | null => {
      const rx = new RegExp(regex);

      return rx.test(c.value)
        ? {
            invalidCharacters: {
              valid: false,
              message
            }
          }
        : null;
    };
  }

  static noValueCollision(values: string[], collisionMessage: string): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => {
      const controlValue = c.value;
      if (controlValue) {
        for (const value of values) {
          if (value.toLowerCase() === controlValue.toLowerCase()) {
            return {
              safariNoValueCollision: {
                valid: false,
                collisionMessage
              }
            };
          }
        }
      }
    };
  }

  static requireOne(): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => {
      if (Validators.required(c) !== null) {
        return {
          safariRequired: {
            valid: false,
            message: 'Select one or enter value for "Other"'
          }
        };
      }
    };
  }
  /**
   * This validator is very tightly coupled to our current logic where if you have an array of checkboxes
   * they are all required (consents) and if there are otherControls passed in then either the array of
   * checkboxes is required or other control is required. In the future this can be made more generic and
   * some additional params can be passed in but for MVP this is it.
   * @param otherControls other controls to check against
   * @param validationMessage validation message
   * @returns
   */
  static required(otherControls: AbstractControl[] = [], validationMessage: string): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => {
      if (c instanceof FormGroup) {
        let allValuesEmpty = true;
        const array = c.get('values');
        const hidden = c.get('hidden');

        if (array && hidden) {
          if (hidden['__safariRequiredControls'] == null) {
            hidden['__safariRequiredControls'] = otherControls;
          }

          // Don't validate eagerly - if the main group hasn't been touched, just return
          // Note: the group will be considered touched if any of its controls are touched
          if (!c.touched) {
            return null;
          }

          if (otherControls) {
            for (const control of otherControls) {
              control.setErrors(null);
            }
          }

          hidden.setErrors(null);
          hidden.markAsTouched();
          if (array && array instanceof FormArray) {
            for (const arrayControl of array.controls) {
              if (!arrayControl.value) {
                if (otherControls == null || otherControls.length == 0) {
                  hidden.setErrors({
                    safariRequired: {
                      valid: false,
                      message: validationMessage || 'Value is required'
                    }
                  });
                  return null;
                }
              } else {
                allValuesEmpty = false;
              }
            }
          }
        }
        if (allValuesEmpty) {
          allValuesEmpty = true;
          // If we got this far then the initial formarray had all requirements
          for (const control of otherControls) {
            if (control.value) {
              allValuesEmpty = false;
              break;
            }
          }
          if (allValuesEmpty == true && otherControls?.length > 0) {
            const lastControl = otherControls[otherControls.length - 1];

            lastControl.markAsTouched();
            lastControl.setErrors({
              safariRequired: {
                valid: false,
                message: validationMessage || 'Value is required'
              }
            });
          }
        }
      }
      return null;
    };
  }
  /**
   *
   * @param controlName It's a bit annoying but this has to be the string that is exactly the same
   * as the control name that the validator is attached to. Angular is realy stingy with information
   * we can retrieve from inside a validator and I just couldn't figure out how to get control name
   * from inside this code. Even though we have AbstractControl that represents the control we're validating
   * I couldn't get its "formControlName".
   * @param collisionMessage
   * @returns
   */
  static noControlValueCollisionInArray(controlName: string, collisionMessage: string): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => {
      if (c.parent && c.parent.parent) {
        // Blank/no value doesn't count
        if (!c.value) {
          return;
        }
        const mainArray = c.parent.parent as FormArray;

        for (const formGroup of mainArray.controls) {
          const controlToCheck = formGroup.get(controlName);
          if (controlToCheck != c) {
            if (controlToCheck.value == c.value) {
              return {
                noControlValueCollisionInArray: {
                  valid: false,
                  collisionMessage
                }
              };
            }
          }
        }
      }

      return null;
    };
  }
  static noControlValueCollision(controlToComparePath: AbstractControl | string, collisionMessage: string): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => {
      const controlValue = c.value;

      const controlToCompare = typeof controlToComparePath == 'string' ? c.parent?.controls[controlToComparePath] : controlToComparePath;
      const controlToCompareValue = controlToCompare?.value;
      // refresh the other control since chances are it has the same validator
      this.safeUpdateValueAndValidity(controlToCompare);
      if (controlValue && controlToCompareValue) {
        if (controlToCompareValue.toLowerCase() === controlValue.toLowerCase()) {
          return {
            safariNoControlValueCollision: {
              valid: false,
              collisionMessage
            }
          };
        }
      }
    };
  }
  static noDomainDifference(controlToComparePath: string, collisionMessage: string): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => {
      const controlValue = c.value;
      const controlToCompare = c.parent?.controls[controlToComparePath];
      const controlToCompareValue = controlToCompare?.value;
      if (controlValue && controlToCompareValue) {
        const index1 = controlValue.indexOf('@');
        const index2 = controlToCompareValue.indexOf('@');
        if (index1 > 0 && index2 > 0) {
          const domain1 = controlValue.substr(index1);
          const domain2 = controlToCompareValue.substr(index2);
          if (domain1.toLowerCase() !== domain2.toLowerCase()) {
            return {
              safariNoDomainDifference: {
                valid: false,
                collisionMessage
              }
            };
          }
        }
      }
    };
  }
  static dateEqualOrLessThan(controlToCompare: AbstractControl | Date | string, errorMessage: string): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => SafariValidators.dateLessOrGreaterThan(c, controlToCompare, true, true, true, errorMessage);
  }
  static dateEqualOrGreaterThan(controlToCompare: AbstractControl | Date | string, errorMessage: string): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => SafariValidators.dateLessOrGreaterThan(c, controlToCompare, false, true, true, errorMessage);
  }
  static dateLessThan(controlToCompare: AbstractControl | Date | string, errorMessage: string): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => SafariValidators.dateLessOrGreaterThan(c, controlToCompare, true, false, true, errorMessage);
  }
  static dateGreaterThan(controlToCompare: AbstractControl | Date | string, errorMessage: string): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => SafariValidators.dateLessOrGreaterThan(c, controlToCompare, false, false, true, errorMessage);
  }
  private static dateLessOrGreaterThan(
    c: AbstractControl,
    controlToCompare: AbstractControl | Date | string,
    needsLessThan: boolean,
    isEqualValid: boolean,
    truncateTime: boolean,
    errorMessage: string
  ): any {
    let controlValue = c.value;

    // We are either passing a datepicker or a straight-up date
    let controlToCompareValue = controlToCompare instanceof Date || typeof controlToCompare == 'string' ? controlToCompare : controlToCompare?.value;

    if (controlValue && controlToCompareValue) {
      if (typeof controlValue == 'string') {
        controlValue = new Date(controlValue);
      } else {
        // If this is already a date control make sure to clone it, since we'll be clearing hours
        controlValue = new Date(controlValue.getTime());
      }
      if (typeof controlToCompareValue == 'string') {
        controlToCompareValue = new Date(controlToCompareValue);
      }
      // If neither string nor instance of Date then this is a datepicker
      else if (!(controlToCompareValue instanceof Date)) {
        // If this is already a date control make sure to clone it, since we'll be clearing hours
        controlToCompareValue = new Date(controlToCompareValue.getTime());
      }
      // At this point controlToCompareValue will be Date, regardless of what was passed in
      if (truncateTime) {
        (controlValue as Date).setHours(0, 0, 0);
        (controlToCompareValue as Date).setHours(0, 0, 0);
      }
      // Now use getTime on both. Trying to do == on dates seems to compare by ref, which
      // will never be true of course
      controlValue = controlValue.getTime();
      controlToCompareValue = controlToCompareValue.getTime();
      // If they are identical bail out right away
      if (isEqualValid && controlValue == controlToCompareValue) {
        return null;
      }
      // Now just check if controlToCompare is less than the main control
      // and return true or false based on needsLessThan param
      const result = controlToCompareValue < controlValue;
      if ((needsLessThan && result) || (!needsLessThan && !result)) {
        return {
          safariDateLessThan: {
            valid: false,
            errorMessage
          }
        };
      }
    }
  }

  static maxDateRangeDays(c1: AbstractControl, c2: AbstractControl, maxDateRangeDays: number, errorMessage?: string): any {
    return (c: AbstractControl): { [key: string]: any } => {
      const dateTime1 = typeof c1.value == 'string' ? DateTime.fromFormat(c1.value, "yyyy'-'MM'-'dd") : DateTime.fromJSDate(c1.value);
      const dateTime2 = typeof c2.value == 'string' ? DateTime.fromFormat(c2.value, "yyyy'-'MM'-'dd") : DateTime.fromJSDate(c2.value);

      if (dateTime1.isValid && dateTime2.isValid && dateTime2.diff(dateTime1, 'days').days <= maxDateRangeDays) {
        return null;
      }

      return {
        safariDateRange: {
          valid: false,
          errorMessage: errorMessage ?? `Maximum date range is ${maxDateRangeDays} days.`
        }
      };
    };
  }

  static canNotSetValue(valueNotAllowed: any | any[], validationMessage: string): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => {
      const controlValue = JSON.stringify(c.value);
      const valuesToCheck = Array.isArray(valueNotAllowed) ? valueNotAllowed : [valueNotAllowed];
      for (const valueToCheck of valuesToCheck) {
        if (controlValue === JSON.stringify(valueToCheck)) {
          return {
            canNotSetValue: {
              valid: false,
              validationMessage
            }
          };
        }
      }
    };
  }
  static noSafariDomain(c: AbstractControl): { [key: string]: any } {
    const email = c.value;
    if (email && email.indexOf('@') !== -1) {
      // eslint-disable-next-line @typescript-eslint/naming-convention, no-unused-vars, @typescript-eslint/no-unused-vars -- destructuring
      const [_, domain] = email.split('@');
      if (domain.toLowerCase() === 'safarilaw.com') {
        return {
          safariDomainNotValid: {
            valid: false
          }
        };
      }
    }
  }
  static email(control: AbstractControl) {
    if (control.value) {
      return isEmail(control.value) ? null : { email: true };
    } else {
      return null;
    }
  }
  static range(prms: { min: number; max: number } = { min: null, max: null }): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      const val: number = control.value;
      if (val == null) {
        return null;
      }
      const validationErrorObject = { range: { validationMessage: `${prms.min}-${prms.max}` } };
      if (isNaN(val) || /\D/.test(val.toString())) {
        return validationErrorObject;
      } else if (!isNaN(prms.min) && !isNaN(prms.max)) {
        return val < prms.min || val > prms.max ? validationErrorObject : null;
      } else if (!isNaN(prms.min)) {
        return val < prms.min ? validationErrorObject : null;
      } else if (!isNaN(prms.max)) {
        return val > prms.max ? validationErrorObject : null;
      } else {
        return null;
      }
    };
  }

  static safeUpdateValueAndValidity(control: AbstractControl) {
    if (!this._recursionControl) {
      this._recursionControl = true;
      control?.updateValueAndValidity();
      this._recursionControl = false;
    }
  }

  static _getCurrencyValueFromControl(controlToCompare: AbstractControl): number {
    let compareValue = controlToCompare.value;
    if (compareValue) {
      if (typeof compareValue == 'string') {
        compareValue = compareValue as unknown as number;
      }
      return compareValue;
    }
  }
  static currencyGreaterThan(controlToCompare: AbstractControl, errorMessage: string, offsetAmount = 0): ValidatorFn {
    const x: number = SafariValidators._getCurrencyValueFromControl(controlToCompare);
    return (c: AbstractControl): { [key: string]: any } => SafariValidators._currencyLessOrGreaterThan(c, x, false, false, errorMessage, offsetAmount);
  }
  static currencyGreaterThanValue(value: number, errorMessage: string, offsetAmount = 0): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => SafariValidators._currencyLessOrGreaterThan(c, value, false, false, errorMessage, offsetAmount);
  }
  static currencyGreaterThanZero(errorMessage: string, offsetAmount = 0): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => SafariValidators._currencyLessOrGreaterThan(c, 0, false, false, errorMessage, offsetAmount);
  }
  static currencyGreaterThanOrEqualValue(value: number, errorMessage: string, offsetAmount = 0): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => SafariValidators._currencyLessOrGreaterThan(c, value, false, true, errorMessage, offsetAmount);
  }
  static currencyGreaterThanOrEqualZero(errorMessage: string, offsetAmount = 0): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => SafariValidators._currencyLessOrGreaterThan(c, 0, false, true, errorMessage, offsetAmount);
  }
  static currencyLessThan(controlToCompare: AbstractControl, errorMessage: string, offsetAmount = 0): ValidatorFn {
    const x: number = SafariValidators._getCurrencyValueFromControl(controlToCompare);
    return (c: AbstractControl): { [key: string]: any } => SafariValidators._currencyLessOrGreaterThan(c, x, true, false, errorMessage, offsetAmount);
  }
  static currencyLessThanOrEqual(controlToCompare: AbstractControl, errorMessage: string, offsetAmount = 0): ValidatorFn {
    const x: number = SafariValidators._getCurrencyValueFromControl(controlToCompare);
    return (c: AbstractControl): { [key: string]: any } => SafariValidators._currencyLessOrGreaterThan(c, x, true, true, errorMessage, offsetAmount);
  }
  static currencyLessThanValue(value: number, errorMessage: string, offsetAmount = 0): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => SafariValidators._currencyLessOrGreaterThan(c, value, true, false, errorMessage, offsetAmount);
  }
  static currencyLessThanOrEqualValue(value: number, errorMessage: string, offsetAmount = 0): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => SafariValidators._currencyLessOrGreaterThan(c, value, true, true, errorMessage, offsetAmount);
  }
  static currencyLessThanZero(errorMessage: string, offsetAmount = 0): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => SafariValidators._currencyLessOrGreaterThan(c, 0, true, false, errorMessage, offsetAmount);
  }
  static currencyLessThanOrEqualZero(errorMessage: string, offsetAmount = 0): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => SafariValidators._currencyLessOrGreaterThan(c, 0, true, true, errorMessage, offsetAmount);
  }
  static currencyNotEqualValue(compareValue: number, errorMessage: string, offsetAmount = 0): ValidatorFn {
    return (c: AbstractControl): { [key: string]: any } => {
      if (c.value == null || c.value === '' || compareValue == null) {
        return undefined;
      }
      // cast to a real number
      let controlValue: number = null;
      if (typeof c.value == 'string') {
        controlValue = parseFloat(c.value);
      } else if (typeof c.value == 'number') {
        controlValue = Number(c.value);
      } else {
        return undefined;
      }
      // not sure this could ever be possible, but check anyway
      if (isNaN(controlValue)) {
        return undefined;
      }
      controlValue += offsetAmount;

      const areEqual = controlValue == compareValue;
      // First check if equal and that is valid.  If so, drop out now

      if (areEqual) {
        return {
          safariCurrencyLessOrGreaterValidation: {
            valid: false,
            errorMessage
          }
        };
      } else {
        // valid result is always null
        return null;
      }
    };
  }
  private static _currencyLessOrGreaterThan(c: AbstractControl, compareValue: number, needsLessThan: boolean, isEqualValid: boolean, errorMessage: string, offsetAmount: number): any {
    // bail if any of the values are invalid.  careful not to let '' be converted to 0
    if (c.value == null || c.value === '' || compareValue == null) {
      return undefined;
    }
    // cast to a real number
    let controlValue: number = null;
    if (typeof c.value == 'string') {
      controlValue = parseFloat(c.value);
    } else if (typeof c.value == 'number') {
      controlValue = Number(c.value);
    } else {
      return undefined;
    }
    // not sure this could ever be possible, but check anyway
    if (isNaN(controlValue)) {
      return undefined;
    }
    controlValue += offsetAmount;
    // now we can check the value
    const isLessThan = controlValue < compareValue;
    const areEqual = controlValue == compareValue;
    // First check if equal and that is valid.  If so, drop out now
    if (isEqualValid && areEqual) {
      return null;
    }
    if ((needsLessThan && !isLessThan) || (!needsLessThan && isLessThan) || areEqual) {
      return {
        safariCurrencyLessOrGreaterValidation: {
          valid: false,
          errorMessage
        }
      };
    } else {
      // valid result is always null
      return null;
    }
  }
}
